import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { THEMES, buildThemeFromBranding } from '../tokens/themes';
import { setActiveTheme } from '../tokens/design';
import { applyFontTokens, fontTokensForBranding } from '../tokens/fonts';
import { useBranding } from './BrandingContext';

const ThemeContext = createContext(null);
const APPEARANCE_KEY = 'sentinel-command:appearance';

function darkBrandingTheme(branding) {
  const base = buildThemeFromBranding(branding);
  return {
    ...base,
    C: {
      ...THEMES.secureview.C,
      brand: base.C.brand,
      brandBg: base.C.brandBg,
      sky: base.C.sky,
      skyBg: base.C.skyBg,
      pur: base.C.pur,
      purBg: base.C.purBg,
    },
  };
}

export function ThemeProvider({ branding: initialBranding, children }) {
  const contextBranding = useBranding();
  const branding = contextBranding || initialBranding;
  const [themeName, setThemeName] = useState(() => {
    try {
      return sessionStorage.getItem('sentinel_theme') || 'secureview';
    } catch { return 'secureview'; }
  });
  const [appearance, setAppearanceState] = useState(() => {
    try {
      localStorage.setItem(APPEARANCE_KEY, 'light');
    } catch { /* ignore */ }
    return 'light';
  });

  // Keep a render-tick counter so children re-render when theme swaps
  const [, setTick] = useState(0);

  // On mount, apply branding-derived theme to the mutable design singletons
  useEffect(() => {
    if (branding) {
      const brandingTheme = buildThemeFromBranding(branding);
      THEMES._branding = brandingTheme;
      THEMES._branding_dark = darkBrandingTheme(branding);
    }
    if (themeName === 'secureview' && branding) {
      setActiveTheme('_branding');
    } else if (themeName === 'secureview') {
      setActiveTheme('connecton');
    } else {
      setActiveTheme(themeName);
    }
    // A stored color-theme choice must not override the active partner's typography.
    applyFontTokens(fontTokensForBranding(branding?.fontFamily));
    setTick(t => t + 1);
  }, [themeName, branding, appearance]);

  const switchTheme = useCallback((name) => {
    if (!THEMES[name]) return;
    setActiveTheme(name);
    sessionStorage.setItem('sentinel_theme', name);
    setThemeName(name);
  }, []);

  const setAppearance = useCallback((next) => {
    const value = 'light';
    try { localStorage.setItem(APPEARANCE_KEY, value); } catch { /* ignore */ }
    setAppearanceState(value);
  }, []);

  const toggleAppearance = useCallback(() => {
    setAppearance('light');
  }, [appearance, setAppearance]);

  const resetTheme = useCallback(() => {
    sessionStorage.removeItem('sentinel_theme');
    try { localStorage.removeItem(APPEARANCE_KEY); } catch { /* ignore */ }
    setAppearanceState('light');
    if (branding) {
      const brandingTheme = buildThemeFromBranding(branding);
      THEMES._branding = brandingTheme;
      THEMES._branding_dark = darkBrandingTheme(branding);
      setActiveTheme('_branding');
      setThemeName('secureview');
    } else {
      setActiveTheme('secureview');
      setThemeName('secureview');
    }
  }, [branding]);

  const activeThemeName = appearance === 'light' && branding
    ? '_branding'
    : appearance === 'light' && themeName === 'secureview' && !branding
      ? 'connecton'
      : themeName;
  const themeBranding = activeThemeName === '_branding' || activeThemeName === '_branding_dark'
    ? { ...THEMES.secureview.branding, ...THEMES._branding?.branding, ...branding }
    : (THEMES[activeThemeName]?.branding || THEMES.secureview.branding);

  return (
    <ThemeContext.Provider value={{ themeName: activeThemeName, appearance, switchTheme, resetTheme, setAppearance, toggleAppearance, branding: themeBranding }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
